// Multi-page document renderer with viewport culling

import { MultiPageCoordinates, MultiPageContext, PageLayout } from './MultiPageCoordinates';
import { SpaceAwareRenderer, RenderContext } from './SpaceAwareRenderer';
import { BackendLayoutPlan } from './BackendPlanValidator';
import { CoordinateSpace } from './CoordinateSpaces';

export interface MultiPageRenderContext extends RenderContext {
  multiPageContext: MultiPageContext;
  visiblePages: number[];
  pageIndex: number; // Make pageIndex required for multi-page rendering
}

export class MultiPageRenderer {
  /**
   * Render multi-page document with viewport culling
   */
  static renderMultiPageDocument(
    ctx: CanvasRenderingContext2D,
    pages: BackendLayoutPlan[],
    multiPageContext: MultiPageContext
  ): void {
    // Clear canvas
    ctx.clearRect(0, 0, multiPageContext.viewportWidth, multiPageContext.viewportHeight);

    // Get visible pages for culling
    const visiblePages = MultiPageCoordinates.getVisiblePages(multiPageContext);

    console.log(`🔍 [MultiPageRenderer] Rendering ${visiblePages.length}/${pages.length} visible pages:`, visiblePages);

    // Render each visible page
    for (const pageIndex of visiblePages) {
      const page = pages[pageIndex];
      const pageLayout = multiPageContext.pages[pageIndex];

      if (!page || !pageLayout) {
        console.warn(`⚠️ [MultiPageRenderer] Missing page data for index ${pageIndex}`);
        continue;
      }

      this.renderSinglePage(ctx, page, pageLayout, multiPageContext, pageIndex);
    }

    // Render page separators
    this.renderPageSeparators(ctx, multiPageContext, visiblePages);
  }

  /**
   * Render a single page within multi-page context
   */
  private static renderSinglePage(
    ctx: CanvasRenderingContext2D,
    page: BackendLayoutPlan,
    pageLayout: PageLayout,
    multiPageContext: MultiPageContext,
    pageIndex: number
  ): void {
    ctx.save();

    // Clip to page bounds to prevent content bleeding
    const pageTop = pageLayout.offsetY - multiPageContext.scrollOffset.y;
    ctx.beginPath();
    ctx.rect(0, pageTop, pageLayout.pageWidth, pageLayout.pageHeight);
    ctx.clip();

    // Render page background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, pageTop, pageLayout.pageWidth, pageLayout.pageHeight);

    // Render page border
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, pageTop, pageLayout.pageWidth, pageLayout.pageHeight);

    // Render margin guides for this page
    this.renderPageMarginGuides(ctx, pageLayout, multiPageContext);

    // Create render context for this page
    const renderContext: MultiPageRenderContext = {
      ctx,
      contentRect: pageLayout.contentRect,
      pageIndex: pageIndex,
      pageHeight: pageLayout.pageHeight,
      pageGap: multiPageContext.pageGap,
      multiPageContext,
      visiblePages: [pageIndex]
    };

    // Render page content using space-aware renderer
    this.renderPageContent(page, renderContext);

    ctx.restore();

    console.log(`✅ [MultiPageRenderer] Rendered page ${pageIndex} at offset ${pageLayout.offsetY}`);
  }

  /**
   * Render content for a single page with multi-page coordinate transforms
   */
  private static renderPageContent(
    page: BackendLayoutPlan,
    renderContext: MultiPageRenderContext
  ): void {
    const { ctx, multiPageContext, pageIndex } = renderContext;

    // Validate pageIndex
    if (pageIndex === undefined || pageIndex < 0 || pageIndex >= multiPageContext.pages.length) {
      console.warn(`⚠️ [MultiPageRenderer] Invalid pageIndex: ${pageIndex}`);
      return;
    }

    // Render each line with multi-page coordinate transforms
    for (const line of page.pages[0].frames[0].lines) {
      for (const run of line.runs) {
        if (!run.text || typeof run.text !== 'string') {
          continue;
        }

        // Transform content coordinates to viewport coordinates
        const contentCoords = {
          x: run.x,
          y: 0, // Y comes from baseline
          space: CoordinateSpace.CONTENT as const
        };

        const viewportCoords = MultiPageCoordinates.transformMultiPage(
          contentCoords,
          CoordinateSpace.VIEWPORT,
          {
            pageIndex: pageIndex,
            multiPageContext,
            contentRect: renderContext.contentRect
          }
        );

        // Calculate baseline Y in viewport coordinates
        const currentPage = multiPageContext.pages[pageIndex];
        if (!currentPage) {
          console.warn(`⚠️ [MultiPageRenderer] Page ${pageIndex} not found`);
          continue;
        }
        
        const baselineViewportY = line.baselineY_pagePx + 
          currentPage.offsetY - 
          multiPageContext.scrollOffset.y;

        // Set font properties
        const fontSize = run.fontSizePx || page.fontSizePx;
        const fontFamily = run.fontFamily || page.fontFamily;
        ctx.font = `${fontSize}px "${fontFamily}"`;
        ctx.fillStyle = '#000000';
        ctx.textBaseline = 'alphabetic';

        // Render the text
        ctx.fillText(
          run.text,
          viewportCoords.x - multiPageContext.scrollOffset.x,
          baselineViewportY
        );
      }
    }
  }

  /**
   * Render margin guides for a specific page
   */
  private static renderPageMarginGuides(
    ctx: CanvasRenderingContext2D,
    pageLayout: PageLayout,
    multiPageContext: MultiPageContext
  ): void {
    const { contentRect, offsetY } = pageLayout;
    const { scrollOffset } = multiPageContext;

    ctx.save();
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);

    const pageTop = offsetY - scrollOffset.y;

    // Top margin line
    ctx.beginPath();
    ctx.moveTo(0, pageTop + contentRect.y);
    ctx.lineTo(pageLayout.pageWidth, pageTop + contentRect.y);
    ctx.stroke();

    // Bottom margin line
    const bottomY = pageTop + contentRect.y + contentRect.height;
    ctx.beginPath();
    ctx.moveTo(0, bottomY);
    ctx.lineTo(pageLayout.pageWidth, bottomY);
    ctx.stroke();

    // Left margin line
    ctx.beginPath();
    ctx.moveTo(contentRect.x, pageTop);
    ctx.lineTo(contentRect.x, pageTop + pageLayout.pageHeight);
    ctx.stroke();

    // Right margin line
    const rightX = contentRect.x + contentRect.width;
    ctx.beginPath();
    ctx.moveTo(rightX, pageTop);
    ctx.lineTo(rightX, pageTop + pageLayout.pageHeight);
    ctx.stroke();

    ctx.restore();
  }

  /**
   * Render visual separators between pages
   */
  private static renderPageSeparators(
    ctx: CanvasRenderingContext2D,
    multiPageContext: MultiPageContext,
    visiblePages: number[]
  ): void {
    ctx.save();
    ctx.fillStyle = '#f3f4f6'; // Light gray separator
    
    for (const pageIndex of visiblePages) {
      if (pageIndex < multiPageContext.pages.length - 1) {
        const currentPage = multiPageContext.pages[pageIndex];
        const separatorY = currentPage.offsetY + currentPage.pageHeight - multiPageContext.scrollOffset.y;
        
        // Only render separator if it's visible
        if (separatorY >= -multiPageContext.pageGap && separatorY <= multiPageContext.viewportHeight) {
          ctx.fillRect(
            0,
            separatorY,
            multiPageContext.viewportWidth,
            multiPageContext.pageGap
          );
        }
      }
    }

    ctx.restore();
  }

  /**
   * Calculate scroll bounds for multi-page document
   */
  static calculateScrollBounds(multiPageContext: MultiPageContext): {
    maxScrollY: number;
    totalHeight: number;
  } {
    const totalHeight = MultiPageCoordinates.calculateTotalDocumentHeight(multiPageContext);
    const maxScrollY = Math.max(0, totalHeight - multiPageContext.viewportHeight);

    return { maxScrollY, totalHeight };
  }

  /**
   * Handle scroll events for multi-page documents
   */
  static handleScroll(
    scrollY: number,
    multiPageContext: MultiPageContext
  ): MultiPageContext {
    const { maxScrollY } = this.calculateScrollBounds(multiPageContext);
    
    // Clamp scroll position
    const clampedScrollY = Math.max(0, Math.min(scrollY, maxScrollY));

    return {
      ...multiPageContext,
      scrollOffset: {
        ...multiPageContext.scrollOffset,
        y: clampedScrollY
      }
    };
  }

  /**
   * Get page information at a specific viewport coordinate
   */
  static getPageInfoAtViewportCoordinate(
    viewportX: number,
    viewportY: number,
    multiPageContext: MultiPageContext
  ): {
    pageIndex: number;
    pageX: number;
    pageY: number;
    isInContent: boolean;
  } | null {
    // Adjust for scroll offset
    const documentY = viewportY + multiPageContext.scrollOffset.y;
    
    const pageInfo = MultiPageCoordinates.findPageAtViewportY(documentY, multiPageContext);
    if (!pageInfo) return null;

    const page = multiPageContext.pages[pageInfo.pageIndex];
    const { contentRect } = page;

    // Check if coordinate is within content area
    const isInContent = 
      viewportX >= contentRect.x &&
      viewportX <= contentRect.x + contentRect.width &&
      pageInfo.pageY >= contentRect.y &&
      pageInfo.pageY <= contentRect.y + contentRect.height;

    return {
      pageIndex: pageInfo.pageIndex,
      pageX: viewportX,
      pageY: pageInfo.pageY,
      isInContent
    };
  }
}
